import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API } from '@/utils/API';

interface TOTPSetupProps {
  onBack: () => void;
  secret: string;
}

const TOTPSetup: React.FC<TOTPSetupProps> = ({ onBack, secret }) => {
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [_step, setStep] = useState<'setup' | 'verify'>('setup');

  const encodedIssuer = encodeURIComponent('OneVerse');
  const qrCodeData = `otpauth://totp/${encodedIssuer}?secret=${secret}&issuer=${encodedIssuer}&algorithm=SHA1&digits=6&period=30`;

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await API.authenticated.fetch('/mfa/totp', {
        method: 'PUT',
        body: JSON.stringify({
          totp_code: verificationCode,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setStep('verify');
      } else {
        setError(data.error || 'Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.' + err);
    } finally {
      setIsVerifying(false);
    }
  };

  if (success) {
    return (
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          TOTP Setup Complete!
        </h2>
        <p className="text-gray-600 mb-6">
          Your authenticator app has been successfully configured. You can now
          use it for multi-factor authentication.
        </p>
        <Button onClick={() => window.location.reload()} className="w-full">
          Continue
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Set up Authenticator App
        </h2>
        <p className="text-gray-600">
          Scan the QR code with your authenticator app to set up two-factor
          authentication.
        </p>
      </div>
      <>
        {/* QR Code Display */}
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
            <QRCodeSVG
              value={qrCodeData}
              size={200}
              level="M"
              includeMargin={true}
            />
          </div>
        </div>

        {/* Manual Secret Entry */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            Can&apos;t scan the QR code? Enter this code manually:
          </Label>
          <div className="flex items-center space-x-2">
            <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
              {secret}
            </code>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">
            Setup Instructions:
          </h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>
              Install an authenticator app like Google Authenticator, Authy, or
              1Password
            </li>
            <li>Open the app and scan the QR code above</li>
            <li>Or manually enter the code shown above</li>
            <li>Enter the 6-digit code from your app below to verify</li>
          </ol>
        </div>

        {/* Verification Code Input */}
        <div className="space-y-4">
          <div>
            <Label
              htmlFor="verification-code"
              className="text-sm font-medium text-gray-700"
            >
              Enter verification code from your authenticator app:
            </Label>
            <Input
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
                setError('');
              }}
              placeholder="123456"
              className="mt-1 text-center text-lg tracking-widest"
              maxLength={6}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex space-x-3">
            <Button variant="outline" onClick={onBack} className="flex-1">
              Back
            </Button>
            <Button
              onClick={handleVerifyCode}
              disabled={verificationCode.length !== 6 || isVerifying}
              className="flex-1"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                'Verify & Complete'
              )}
            </Button>
          </div>
        </div>
      </>
    </div>
  );
};

export default TOTPSetup;
